import React, { useMemo } from "react";
import Select from "react-select";

import CommissionModalShell from "./commission-modal-shell";
import CommissionSplitSection from "./commission-split-section";
import { CalcRow, MoneyInput, NoteInput, PercentInput } from "./commission-fields";
import { isSplitValid, toNumber, toSplitForm, toSplitPayload } from "./commission-calc";
import useCommissionEditor from "./use-commission-editor";
import currencyFormatter from "../../../../helpers/currencyFormatter";

const CLIENT_TYPE_OPTIONS = ["New", "Repeat", "Custom"].map((value) => ({ value, label: value }));

const formatRate = (rate) => (rate === null || rate === undefined ? "-" : `${rate}%`);

const EditNetCommissionModal = ({ isOpen, onClose, trip, breakdown, onSaved }) => {
  const initialForm = useMemo(
    () => ({
      serviceFee: breakdown?.net?.serviceFee ?? 0,
      adjustment: breakdown?.net?.adjustment ?? 0,
      note: breakdown?.net?.note || "",
      split: toSplitForm(breakdown?.net?.split),
      clientType: breakdown?.clientType || "New",
      customPayoutRate: breakdown?.customPayoutRate ?? null,
    }),
    [breakdown]
  );

  const { form, updateForm, dirty, salesReps, repsLoading, saving, save } = useCommissionEditor({
    isOpen,
    tripId: trip?._id,
    section: "net",
    initialForm,
    onSaved,
    onClose,
  });

  const isCustom = form.clientType === "Custom";
  const gross = toNumber(breakdown?.gross);
  const preSplit = gross - toNumber(form.serviceFee) + toNumber(form.adjustment);
  const canSave =
    dirty &&
    form.serviceFee !== null &&
    isSplitValid(form.split) &&
    (!isCustom || toNumber(form.customPayoutRate) > 0);

  const handleSave = () =>
    save({
      serviceFee: toNumber(form.serviceFee),
      adjustment: toNumber(form.adjustment),
      note: form.note.trim(),
      split: toSplitPayload(form.split),
      clientType: form.clientType,
      customPayoutRate: isCustom ? toNumber(form.customPayoutRate) : null,
    });

  return (
    <CommissionModalShell
      isOpen={isOpen}
      title="Edit Net Commission"
      trip={trip}
      dirty={dirty}
      canSave={canSave}
      saving={saving}
      onSave={handleSave}
      onClose={onClose}
      testId="edit-net-commission-modal"
    >
      <table className="tv-table tv-commission-table">
        <tbody>
        <CalcRow label="Gross Commission" value={gross} testId="edit-net-gross"/>
        <tr data-testid="edit-net-service-fee-row">
          <td>
            Service Fee
            <div className="tv-commission-note">
              Bracket: {currencyFormatter.format(toNumber(breakdown?.net?.calculatedServiceFee))}
            </div>
          </td>
          <td>
            <MoneyInput
              value={form.serviceFee}
              allowNegative={false}
              prefix="-$"
              onChange={(serviceFee) => updateForm({ serviceFee })}
              testId="edit-net-service-fee"
            />
          </td>
        </tr>
        <CalcRow label="Adjustment">
          <MoneyInput
            value={form.adjustment}
            onChange={(adjustment) => updateForm({ adjustment })}
            testId="edit-net-adjustment"
          />
        </CalcRow>
        <CalcRow
          label={form.split ? "Pre-split Net Commission" : "Final Net Commission"}
          value={preSplit}
          total
          testId="edit-net-pre-split"
        />
        </tbody>
      </table>

      <NoteInput value={form.note} onChange={(note) => updateForm({ note })} testId="edit-net-note"/>

      <CommissionSplitSection
        title="Split Net Commission"
        finalLabel="Final Net Comm"
        split={form.split}
        onChange={(split) => updateForm({ split })}
        salesReps={salesReps}
        repsLoading={repsLoading}
        owner={trip?.owner}
        preSplit={preSplit}
        testIdPrefix="edit-net"
      />

      <h6 className="mt-20">Edit Payout Details</h6>
      <div className="w-100 d-flex gap-20 flex-wrap" data-testid="edit-net-payout-details">
        <div className="form-group tv-modal-field-half">
          <label>Client Type</label>
          <Select
            className="base-select"
            classNamePrefix="select"
            options={CLIENT_TYPE_OPTIONS}
            value={CLIENT_TYPE_OPTIONS.find(({ value }) => value === form.clientType)}
            onChange={(option) => updateForm({ clientType: option.value })}
            isSearchable={false}
          />
        </div>
        <div className={`form-group tv-modal-field-half${isCustom ? " tv-commission-muted" : ""}`}>
          <label>Rep Tier</label>
          <input className="base-input" value={breakdown?.ownerTier ?? "-"} disabled readOnly/>
        </div>
        <div className={`form-group tv-modal-field-half${isCustom ? " tv-commission-muted" : ""}`}>
          <label>Payout Rate - New</label>
          <input className="base-input" value={formatRate(breakdown?.tierRates?.newRate)} disabled readOnly/>
        </div>
        <div className={`form-group tv-modal-field-half${isCustom ? " tv-commission-muted" : ""}`}>
          <label>Payout Rate - Repeat</label>
          <input className="base-input" value={formatRate(breakdown?.tierRates?.repeatRate)} disabled readOnly/>
        </div>
        {isCustom && (
          <div className="form-group w-100">
            <label>Custom Payout Rate <span className="required">*</span></label>
            <PercentInput
              value={form.customPayoutRate}
              decimalScale={1}
              onChange={(customPayoutRate) => updateForm({ customPayoutRate })}
              placeholder="0%"
              testId="edit-net-custom-rate"
            />
            <div className="tv-commission-note">
              Applies to this trip only - the tier and both tier rates above are ignored.
            </div>
          </div>
        )}
      </div>
    </CommissionModalShell>
  );
};

export default EditNetCommissionModal;
